import { useState, type FormEvent } from 'react';
import { genId } from '@bhorosha/core';
import { backend } from '../data';
import type { Session } from '@bhorosha/data';
import { BhStore } from '@bhorosha/data';
import { parseBackup, writeBackup, type ParsedBackup } from '../backupImport';
import { t, tErr } from '@bhorosha/i18n';
import { AuthCard, ErrorText, Field, Notice } from '../ui/components';

/** First run: create a family (and its first person, or import a backup). */
export function Onboarding({ session, onCreated, onCancel }: { session: Session; onCreated: (familyId: string) => void; onCancel?: () => void }) {
  const [family, setFamily] = useState('');
  const [name, setName] = useState(session.displayName || '');
  const [age, setAge] = useState('');
  const [gender, setGender] = useState('Male');
  const [backup, setBackup] = useState<ParsedBackup | null>(null);
  const [useBackup, setUseBackup] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  async function pickFile(f: File | undefined) {
    setErr(''); setBackup(null);
    if (!f) return;
    const parsed = parseBackup(await f.text());
    if (!parsed) { setErr(t('onb.backupBad')); return; }
    setBackup(parsed);
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setErr('');
    if (useBackup && !backup) { setErr(t('onb.backupBad')); return; }
    setBusy(true);
    try {
      const first = useBackup ? null : { id: genId(), name: name.trim(), age: age.trim(), gender, dialysis_days: [], dialysis_time: 'morning' };
      const fid = await backend.createFamily(family.trim(), first);
      if (useBackup && backup) {
        const store = new BhStore({ familyId: fid, userId: session.userId, role: 'owner', backend, persist: localStorage, debounceMs: 0 });
        await store.refresh();
        writeBackup(store, backup);
        for (let i = 0; i < 5 && store.pending; i++) await store.flush();
        store.saveNow();
        if (store.pending) throw new Error('Some of the backup could not be uploaded. Check your connection and try again.');
      }
      onCreated(fid);
    } catch (ex) {
      setErr(tErr(ex));
    } finally {
      setBusy(false);
    }
  }

  const s = backup?.summary;
  return (
    <AuthCard>
      <h1 className="bh-h1">{t('onb.title')}</h1>
      <p className="bh-muted">{t('onb.intro')}</p>
      <form className="bh-form" onSubmit={submit}>
        <Field label={t('onb.familyName')}>
          <input value={family} onChange={e => setFamily(e.target.value)} placeholder={t('onb.familyPlaceholder')} required autoFocus />
        </Field>

        {!useBackup ? (
          <fieldset className="bh-fieldset">
            <legend>{t('onb.firstPatient')}</legend>
            <Field label={t('onb.patientName')}><input value={name} onChange={e => setName(e.target.value)} required /></Field>
            <div className="bh-row">
              <Field label={t('onb.age')}><input value={age} onChange={e => setAge(e.target.value)} inputMode="numeric" style={{ width: 90 }} /></Field>
              <Field label={t('onb.gender')}>
                <select value={gender} onChange={e => setGender(e.target.value)}>
                  <option value="Male">{t('onb.male')}</option>
                  <option value="Female">{t('onb.female')}</option>
                  <option value="Other">{t('onb.other')}</option>
                </select>
              </Field>
            </div>
            <span className="bh-field-hint">{t('onb.firstPatientHint')}</span>
          </fieldset>
        ) : (
          <fieldset className="bh-fieldset">
            <legend>{t('onb.fromBackup')}</legend>
            <p className="bh-small bh-muted">{t('onb.fromBackupHint')}</p>
            <input type="file" accept=".json,application/json" onChange={e => pickFile(e.target.files?.[0])} />
            {s && <Notice tone="ok">{t('onb.backupLoaded', { summary: `${s.people} people, ${s.medicines} medicines, ${s.reminders} reminders, ${s.visits} visits, ${s.tests} tests` })}</Notice>}
          </fieldset>
        )}
        <button type="button" className="bh-link" onClick={() => { setUseBackup(!useBackup); setErr(''); }}>
          {useBackup ? t('common.back') : t('onb.fromBackup')}
        </button>

        <ErrorText>{err}</ErrorText>
        <button className="bh-btn bh-btn-primary bh-full" disabled={busy} type="submit">{busy ? t('common.loading') : t('onb.create')}</button>
      </form>
      <div className="bh-auth-foot">
        {onCancel
          ? <button className="bh-link" onClick={onCancel}>{t('common.cancel')}</button>
          : <span className="bh-small bh-muted">{t('onb.invitedHint')}</span>}
      </div>
    </AuthCard>
  );
}
